import React, { useState, useMemo, useEffect } from 'react';
import FilterIcon from '../../SVG/Filter';

const BalanceHistorySection = ({ model }) => {
    const allClaims = model?.balanceHistory || [];

    // pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    const columns = [
        { label: 'Entry Date', accessor: 'entryDateDisplay' },
        { label: 'Category', accessor: 'category' },
        { label: 'Amount', accessor: 'amountDisplay' },
    ];

    // no filters � use raw data
    const filteredClaims = allClaims;

    // pagination calculations
    const totalEntries = filteredClaims.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage) || 1;

    const paginatedClaims = useMemo(() => {
        const startIdx = (currentPage - 1) * rowsPerPage;
        return filteredClaims.slice(startIdx, startIdx + rowsPerPage);
    }, [filteredClaims, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredClaims]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    //--- Export Feature ---
    const exportCsv = (rows, filename = "statements.csv") => {
        if (!rows || rows.length === 0) {
            alert("No data to export");
            return;
        }

        const headers = [
            'entryDate',
            'category',
            'accountDesc',
            'amount',
            'runningAmount'
        ];

        const csvContent = [
            headers.join(","),
            ...rows.map(row =>
                headers
                    .map(h => `"${(row[h] ?? "").toString().replace(/"/g, '""')}"`)
                    .join(",")
            )
        ].join("\r\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const from = totalEntries === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);

    return (
        <div className="container container-section" id="balance-history-container">
            {allClaims.length === 0 ? (
                <div className="card notfoundbox rounded-3 shadow-lg">
                    <div className="card-body">
                        <div className="flex-container">
                            <h5>No Balance History Found</h5>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* EXPORT ONLY */}
                    <div className="filter-box">
                        <FilterIcon />
                        <div className="filter-button-box">
                            <button
                                type="button"
                                className="filterButton"
                                onClick={() => exportCsv(filteredClaims, "statements_export.csv")}
                            >
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* TABLE (non-sortable) */}
                    <div className="paginated-table-container">
                        <table className="paginated-table">
                            <thead>
                                <tr>
                                    {columns.map(col => (
                                        <th key={col.accessor}>{col.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedClaims.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{row.entryDateDisplay}</td>
                                        <td>{row.category}</td>
                                        <td>{row.amountDisplay}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* PAGINATION BAR */}
                        <div className="pagination-bar">
                            <div className="pagination-summary">
                                    Showing {from}-{to} of {totalEntries} entries
                            </div>

                            <div className="pagination-controls">
                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                                    &#171;
                                </button>
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                    &#8592;
                                </button>

                                {[...Array(totalPages)].map((_, i) => {
                                    const page = i + 1;
                                    if (page < currentPage - 1 || page > currentPage + 1) return null;

                                    return (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className={currentPage === page ? "active" : ""}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                                    &#8594;
                                </button>
                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                                    &#187;
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default BalanceHistorySection;
